import {
	getAIFCContentType,
	isCreatableAction,
} from '@atlassian/conversation-assistant-action-helpers/utils';
import { getConfluenceContentIdFromUrl } from '@atlassian/conversation-assistant-action-helpers/utils/content-url';
import {
	getActionContentId,
	getActionTitle,
} from '@atlassian/conversation-assistant-action-helpers/utils/get-action-data';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import {
	isStoreAssistant,
	type StoreAssistant,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

export type StagedContentLinkMatch = {
	invocationId: string;
	/** The id of the assistant message whose action this match came from. */
	messageId: string;
	/** The staged item's content id (e.g. a Confluence page id). */
	contentId: string;
	/** The staged item's current title, per its last-known creatable action. */
	title: string;
	/** The staged item's content type, e.g. `'confluence-page'`. */
	contentType: ReturnType<typeof getAIFCContentType>;
	/** The matched action itself, so callers can derive flags (MAUI, Jira/JSM, status) from it. */
	action: MessageAction;
};

/**
 * Invocation ids with a successful "Action executed" message anywhere in the conversation. This is
 * the backend's record of a finalize, and is present after a history reload.
 */
const getSuccessfullyExecutedInvocationIds = (messages: StoreChatItem[]): ReadonlySet<string> => {
	const invocationIds = new Set<string>();

	for (const message of messages) {
		if (!isStoreAssistant(message)) {
			continue;
		}

		const metadata = message.content.message.message.message_metadata;
		if (
			metadata?.is_action_execution &&
			metadata.action_execution_result === 'SUCCESS' &&
			metadata.invocation_id
		) {
			invocationIds.add(metadata.invocation_id);
		}
	}

	return invocationIds;
};

/**
 * Whether a creatable action has been finalized (published out of the Rovo system space), judged
 * from signals available synchronously:
 * 1. `invokedActions[invocationId] === 'finished'` on the owning message, set live in this session.
 * 2. A successful "Action executed" message for the same invocation, present after a reload.
 *
 * `action.status` isn't used: `finished` only means the create tool call finished streaming, which
 * databases and whiteboards reach long before they are finalized.
 *
 * Known gap: a finalize that doesn't go through either path (e.g. backend-resumption transport)
 * isn't seen until the next history reload.
 */
const isActionFinalized = (
	action: MessageAction,
	owningMessage: StoreAssistant,
	successfullyExecutedInvocationIds: ReadonlySet<string>,
): boolean => {
	if (!action.invocationId) {
		return false;
	}

	if (owningMessage.invokedActions?.[action.invocationId] === 'finished') {
		return true;
	}

	return successfullyExecutedInvocationIds.has(action.invocationId);
};

/**
 * Finds the unfinalized staged item (a creatable action in any message) whose content id is the
 * one in the clicked URL's `/wiki/spaces/<key>/<type>/<id>` path. Finalized items are skipped so
 * their links behave as normal.
 *
 * URLs without a content id are rejected before `messages` is read, since this runs on every
 * non-modifier link click while the experiment is on.
 *
 * Pure, so other surfaces that resolve staged links through a different transport can reuse it.
 */
export const findStagedContentLinkMatch = (
	url: string,
	messages: StoreChatItem[],
): StagedContentLinkMatch | undefined => {
	const urlContentId = getConfluenceContentIdFromUrl(url);

	if (!urlContentId) {
		return undefined;
	}

	let successfullyExecutedInvocationIds: ReadonlySet<string> | undefined;

	for (const message of messages) {
		if (!isStoreAssistant(message)) {
			continue;
		}

		const messageId = message.content.message.message.id;
		const actions = message.content.message.message.actions ?? [];

		for (const action of actions) {
			if (!action.invocationId) {
				continue;
			}

			const contentId = getActionContentId(action);

			if (contentId !== urlContentId || !isCreatableAction(action)) {
				continue;
			}

			// Lazy: this scans every message, so only do it once a URL has matched an action.
			successfullyExecutedInvocationIds ??= getSuccessfullyExecutedInvocationIds(messages);

			if (isActionFinalized(action, message, successfullyExecutedInvocationIds)) {
				continue;
			}

			return {
				invocationId: action.invocationId,
				messageId,
				contentId,
				title: getActionTitle(action),
				contentType: getAIFCContentType(action),
				action,
			};
		}
	}

	return undefined;
};
